import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, QueryList, ViewChildren, input, output } from '@angular/core';

/**
 * Segmented numeric OTP input: one box per digit with auto-advance, backspace,
 * arrow-key navigation, paste-to-fill and browser-autofill distribution. Emits the
 * assembled code on every change and an enter-press event. Ported from the widget's
 * OtpInputComponent for the registration OTP screens (JB-1569).
 */
@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpInputComponent implements OnInit, AfterViewInit {

  readonly length = input(6);
  readonly disabled = input(false);
  readonly invalid = input(false);
  // Accessible name for the box group + each digit box, passed in already
  // translated by the parent (the visible field label). Replaces a hardcoded
  // English "OTP digit" so screen-reader users hear the localized label.
  readonly ariaLabel = input<string>('Verification code');
  readonly otpChange = output<string>();
  readonly otpEnterPress = output<void>();

  protected otpValues: string[] = [];

  @ViewChildren('otpInput') private otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  ngOnInit(): void {
    this.otpValues = Array(this.length()).fill('');
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.otpInputs.first?.nativeElement.focus());
  }

  /** Refocus the first box (e.g. after a resend). */
  focus(): void {
    this.otpInputs.first?.nativeElement.focus();
  }

  /** Clear every box (e.g. after a resend). */
  clear(): void {
    this.otpValues = Array(this.length()).fill('');
    this.otpInputs.forEach(ref => (ref.nativeElement.value = ''));
    this.emit();
  }

  protected onInput(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const clean = target.value.replace(/[^0-9]/g, '');
    // The whole code arriving in one box (browser autofill / a fast paste that fired
    // as input) → distribute it across the boxes.
    if ((index === 0 && clean.length > 1) || clean.length >= this.length()) {
      this.fill(clean);
      return;
    }
    const value = clean.charAt(0) || '';
    target.value = value;
    this.otpValues[index] = value;
    if (value && index < this.length() - 1) {
      this.inputAt(index + 1)?.focus();
    }
    this.emit();
  }

  protected onFocus(event: FocusEvent, index: number): void {
    if (this.otpValues[index]) {
      (event.target as HTMLInputElement).select();
    }
  }

  protected onKeyDown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.inputAt(index + 1)?.focus();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.inputAt(index - 1)?.focus();
        break;
      case 'Backspace':
        if (this.otpValues[index]) {
          this.otpValues[index] = '';
          this.setBox(index, '');
          this.emit();
        } else if (index > 0) {
          this.inputAt(index - 1)?.focus();
          this.otpValues[index - 1] = '';
          this.setBox(index - 1, '');
          this.emit();
        }
        break;
      case 'Enter':
        event.preventDefault();
        this.otpEnterPress.emit();
        break;
      default:
        // Keyboard shortcuts (Ctrl/Cmd+V paste, copy, select-all, undo) must pass, or
        // the browser never fires the paste event onPaste() relies on.
        if (event.ctrlKey || event.metaKey) {
          break;
        }
        // Allow single digits + control keys; block letters, space, symbols.
        if (!/^[0-9]$/.test(event.key) &&
          !['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Escape', 'Delete'].includes(event.key)) {
          event.preventDefault();
        }
        break;
    }
  }

  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.fill(event.clipboardData?.getData('text') || '');
  }

  private fill(raw: string): void {
    const chars = raw.replace(/[^0-9]/g, '').slice(0, this.length()).split('');
    this.otpValues = Array(this.length()).fill('');
    chars.forEach((char, i) => {
      this.otpValues[i] = char;
      this.setBox(i, char);
    });
    const next = chars.length < this.length() ? chars.length : this.length() - 1;
    setTimeout(() => this.inputAt(next)?.focus());
    this.emit();
  }

  private inputAt(index: number): HTMLInputElement | undefined {
    if (index < 0 || index >= this.length()) {
      return undefined;
    }
    return this.otpInputs.toArray()[index]?.nativeElement;
  }

  private setBox(index: number, value: string): void {
    const el = this.inputAt(index);
    if (el) {
      el.value = value;
    }
  }

  private emit(): void {
    this.otpChange.emit(this.otpValues.join(''));
  }
}
